import React, { useState } from 'react';
import { Modal, Button } from '@/components/base';
import { Input } from '@/components/base/Form/Input';
import { Search, UserPlus, Mic } from 'lucide-react';
import { medplumResourcesApi, type PatientResource } from '@/lib/api/medplum-resources';
import { createComponentLogger } from '@/lib/utils/logger';
import { formatGenderDisplay, calculateAge, getPatientNameFromFhir } from '@/lib/types/patient';
import { formatLabel } from '@/lib/utils/format';
import type { PatientData } from './ScheduleAppointmentModal';
import { useAuthStore } from '@/lib/stores/authStore';
import { sanitizePhoneInput } from '@/lib/utils/phone';

const logger = createComponentLogger('PatientSearchModal');

// Re-export PatientData for consumers that only need PatientSearchModal
export type { PatientData as PatientSearchResult } from './ScheduleAppointmentModal';

/** Maps a FHIR PatientResource to the app's PatientData format, extracting the first identifier value */
function mapFhirPatientToPatientData(patient: PatientResource): PatientData {
  const fullName = getPatientNameFromFhir(patient.name) || 'Unknown';
  const age = patient.birthDate ? calculateAge(patient.birthDate) : 0;
  const phone = patient.telecom?.find((t) => t.system === 'phone')?.value;

  // Extract identifier value from the first stored identifier
  let identifier: string | null = null;
  if (patient.identifier && patient.identifier.length > 0) {
    identifier = patient.identifier[0]?.value ?? null;
  }

  let gender: 'M' | 'F' | 'O' | 'U' = 'M';
  if (patient.gender === 'female') gender = 'F';
  else if (patient.gender === 'other') gender = 'O';
  else if (patient.gender === 'unknown') gender = 'U';

  return {
    uuid: patient.id!,
    name: fullName,
    age,
    gender,
    phoneNumber: phone,
    identifier,
  };
}

export interface PatientSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatientSelected: (patient: PatientData) => void;
  title?: string;
  description?: string;
  /** Callback to switch to patient creation when patient not found */
  onSwitchToCreatePatient?: (phoneNumber: string) => void;
  /** Callback to start consultation directly from patient search (skips scheduling) */
  onStartConsultation?: (patient: PatientData) => Promise<void> | void;
  /** Whether a consultation is currently being started */
  isStartingConsultation?: boolean;
}

/**
 * Reusable Patient Search Modal
 * Searches for patients by phone number and returns the selected patient.
 * Supports multiple patients with the same phone number.
 */
export const PatientSearchModal: React.FC<PatientSearchModalProps> = ({
  isOpen,
  onClose,
  onPatientSelected,
  title = 'Search Patient',
  description = 'Search for a patient by phone number',
  onSwitchToCreatePatient,
  onStartConsultation,
  isStartingConsultation = false,
}) => {
  const projectConfig = useAuthStore((state) => state.projectConfig);
  const patientIdentifierConfig = projectConfig?.identifierLabel
    ? { label: projectConfig.identifierLabel }
    : undefined;

  const [phoneSearch, setPhoneSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [foundPatients, setFoundPatients] = useState<PatientData[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<PatientData | null>(null);
  const [patientNotFound, setPatientNotFound] = useState(false);

  // Identifier search state (only used when patientIdentifierConfig is set)
  const [identifierSearch, setIdentifierSearch] = useState('');
  const [isSearchingByIdentifier, setIsSearchingByIdentifier] = useState(false);
  const [identifierSearchError, setIdentifierSearchError] = useState<string | null>(null);
  const [identifierPatientNotFound, setIdentifierPatientNotFound] = useState(false);

  const handleSearch = async () => {
    if (!phoneSearch.trim()) {
      setSearchError('Please enter a phone number');
      return;
    }

    // Validate phone number is exactly 10 digits
    if (phoneSearch.length !== 10) {
      setSearchError('Phone number must be exactly 10 digits');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setFoundPatients([]);
    setSelectedPatient(null);
    setPatientNotFound(false);

    try {
      const patients = await medplumResourcesApi.searchPatientByPhone(phoneSearch.trim());

      if (patients.length === 0) {
        setSearchError('No patient found with this phone number');
        setPatientNotFound(true);
        return;
      }

      const mapped = patients.map((p) => mapFhirPatientToPatientData(p));
      setFoundPatients(mapped);
      // Auto-select the first patient
      setSelectedPatient(mapped[0]);
      logger.log(`Found ${mapped.length} patient(s) for phone:`, phoneSearch);
    } catch (error) {
      logger.error('Failed to search patient:', error);
      setSearchError('Failed to search patient. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleIdentifierSearch = async () => {
    if (!identifierSearch.trim() || !patientIdentifierConfig) return;

    setIsSearchingByIdentifier(true);
    setIdentifierSearchError(null);
    setFoundPatients([]);
    setSelectedPatient(null);
    setIdentifierPatientNotFound(false);

    try {
      const patients = await medplumResourcesApi.searchPatientByIdentifier(identifierSearch.trim());

      if (patients.length === 0) {
        setIdentifierSearchError(`No patient found with this ${patientIdentifierConfig.label}`);
        setIdentifierPatientNotFound(true);
        return;
      }

      const mapped = patients.map((p) => mapFhirPatientToPatientData(p));
      setFoundPatients(mapped);
      setSelectedPatient(mapped[0]);
      logger.log(`Found ${mapped.length} patient(s) for identifier:`, identifierSearch);
    } catch (error) {
      logger.error('Failed to search patient by identifier:', error);
      setIdentifierSearchError('Failed to search patient. Please try again.');
    } finally {
      setIsSearchingByIdentifier(false);
    }
  };

  const handleConfirm = () => {
    if (selectedPatient) {
      onPatientSelected(selectedPatient);
      handleClose();
    }
  };

  const handleStartConsultation = async () => {
    if (selectedPatient && onStartConsultation) {
      try {
        const result = onStartConsultation(selectedPatient);
        // Wait for async callback if it returns a Promise
        if (result instanceof Promise) {
          await result;
        }
        handleClose();
      } catch (error) {
        logger.error('Error starting consultation:', error);
        // Don't close modal on error - let parent handle it
      }
    }
  };

  const handleClose = () => {
    setPhoneSearch('');
    setSearchError(null);
    setFoundPatients([]);
    setSelectedPatient(null);
    setPatientNotFound(false);
    setIdentifierSearch('');
    setIdentifierSearchError(null);
    setIdentifierPatientNotFound(false);
    onClose();
  };

  const handleCreatePatient = () => {
    if (onSwitchToCreatePatient) {
      onSwitchToCreatePatient(phoneSearch.trim());
      handleClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !isSearching && !open && handleClose()}
      title={title}
      description={description}
      disableOutsideClick={true}
      footer={
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isSearching}
            className="flex-1"
          >
            Cancel
          </Button>
          {onStartConsultation && selectedPatient && (
            <Button
              type="button"
              variant="secondary"
              disabled={isSearching || isStartingConsultation}
              loading={isStartingConsultation}
              className="flex-1 bg-[#6b8af9] text-white hover:bg-[#5a7ce6]"
              onClick={handleStartConsultation}
            >
              <Mic className="mr-2 h-4 w-4" />
              Start Consultation
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            disabled={!selectedPatient || isSearching}
            className="flex-1 bg-[#061a61] hover:bg-[#061a61]/90"
            onClick={handleConfirm}
          >
            Continue to Schedule
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Phone Search */}
        <div>
          <label className="mb-2 block font-hanken text-sm font-medium text-gray-700">
            Patient Phone Number <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <Input
              type="tel"
              placeholder="Enter 10-digit phone number"
              value={phoneSearch}
              onChange={(e) => {
                setPhoneSearch(sanitizePhoneInput(e.target.value));
                setSearchError(null);
                setFoundPatients([]);
                setSelectedPatient(null);
                setPatientNotFound(false);
              }}
              disabled={isSearching || isSearchingByIdentifier}
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={handleSearch}
              disabled={isSearching || isSearchingByIdentifier || phoneSearch.length !== 10}
              loading={isSearching}
              className="bg-[#6b8af9] px-4 text-white hover:bg-[#061a61] disabled:bg-gray-200 disabled:text-gray-400"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
          {searchError && <p className="mt-2 font-hanken text-sm text-red-500">{searchError}</p>}

          {/* Show Create Patient option when patient not found */}
          {patientNotFound && onSwitchToCreatePatient && (
            <div className="mt-3 rounded-lg bg-amber-50 p-3">
              <p className="mb-2 font-hanken text-sm text-amber-700">
                Want to register this patient?
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCreatePatient}
                className="w-full border-amber-200 bg-white text-amber-700 hover:bg-amber-100"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Create New Patient
              </Button>
            </div>
          )}
        </div>

        {/* Book Number Search — only shown when patientIdentifier is configured */}
        {patientIdentifierConfig && (
          <div>
            <label className="mb-2 block font-hanken text-sm font-medium text-gray-700">
              {formatLabel(patientIdentifierConfig.label)}
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder={`Enter ${formatLabel(patientIdentifierConfig.label)}`}
                value={identifierSearch}
                onChange={(e) => {
                  setIdentifierSearch(e.target.value);
                  setIdentifierSearchError(null);
                  setFoundPatients([]);
                  setSelectedPatient(null);
                  setIdentifierPatientNotFound(false);
                }}
                disabled={isSearching || isSearchingByIdentifier}
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleIdentifierSearch();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleIdentifierSearch}
                disabled={isSearching || isSearchingByIdentifier || !identifierSearch.trim()}
                loading={isSearchingByIdentifier}
                className="bg-[#6b8af9] px-4 text-white hover:bg-[#061a61] disabled:bg-gray-200 disabled:text-gray-400"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
            {identifierSearchError && (
              <p className="mt-2 font-hanken text-sm text-red-500">{identifierSearchError}</p>
            )}

            {identifierPatientNotFound && onSwitchToCreatePatient && (
              <div className="mt-3 rounded-lg bg-amber-50 p-3">
                <p className="mb-2 font-hanken text-sm text-amber-700">
                  Want to register this patient?
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCreatePatient}
                  className="w-full border-amber-200 bg-white text-amber-700 hover:bg-amber-100"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create New Patient
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Found Patient(s) */}
        {foundPatients.length === 1 && selectedPatient && (
          // Single patient found - show the same card as before
          <div className="rounded-lg bg-green-50 p-4">
            <p className="mb-2 font-hanken text-sm font-medium text-green-700">Patient Found</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-hanken text-xs text-gray-700">Name</label>
                <p className="font-hanken text-sm font-medium text-gray-900">
                  {selectedPatient.name}
                </p>
              </div>
              <div>
                <label className="font-hanken text-xs text-gray-700">Phone</label>
                <p className="font-hanken text-sm font-medium text-gray-900">
                  {selectedPatient.phoneNumber || 'N/A'}
                </p>
              </div>
              <div>
                <label className="font-hanken text-xs text-gray-700">Age</label>
                <p className="font-hanken text-sm font-medium text-gray-900">
                  {selectedPatient.age} years
                </p>
              </div>
              <div>
                <label className="font-hanken text-xs text-gray-700">Gender</label>
                <p className="font-hanken text-sm font-medium text-gray-900">
                  {formatGenderDisplay(selectedPatient.gender)}
                </p>
              </div>
              {patientIdentifierConfig && selectedPatient.identifier && (
                <div>
                  <label className="font-hanken text-xs text-gray-700">
                    {formatLabel(patientIdentifierConfig.label)}
                  </label>
                  <p className="font-hanken text-sm font-medium text-gray-900">
                    {selectedPatient.identifier}
                  </p>
                </div>
              )}
            </div>

            {/* Option to create a new patient with this number */}
            {onSwitchToCreatePatient && (
              <div className="mt-3 border-t border-green-200 pt-3">
                <p className="mb-2 font-hanken text-xs text-gray-700">
                  Not the right patient? Register a new one with this number.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCreatePatient}
                  className="w-full border-green-200 bg-white text-green-700 hover:bg-green-100"
                  size="sm"
                >
                  <UserPlus className="mr-2 h-3.5 w-3.5" />
                  Register New Patient
                </Button>
              </div>
            )}
          </div>
        )}

        {foundPatients.length > 1 && (
          // Multiple patients found - show selectable list
          <div className="space-y-3">
            <p
              id="patient-selection-label"
              className="font-hanken text-sm font-medium text-gray-700"
            >
              {foundPatients.length} patients found with this phone number. Select one:
            </p>
            <div
              role="radiogroup"
              aria-labelledby="patient-selection-label"
              className="max-h-[200px] space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-2"
            >
              {foundPatients.map((patient) => {
                const isSelected = selectedPatient?.uuid === patient.uuid;
                return (
                  <button
                    key={patient.uuid}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setSelectedPatient(patient)}
                    className={`flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors ${
                      isSelected
                        ? 'bg-[#061a61]/10 ring-2 ring-[#061a61]'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    {/* Radio indicator */}
                    <div
                      aria-hidden="true"
                      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                        isSelected ? 'border-[#061a61] bg-[#061a61]' : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </div>

                    {/* Patient info */}
                    <div className="flex flex-1 items-center justify-between">
                      <div>
                        <span className="font-hanken text-sm font-medium text-gray-900">
                          {patient.name}
                        </span>
                        {patientIdentifierConfig && patient.identifier && (
                          <span className="ml-2 font-hanken text-xs text-gray-700">
                            {formatLabel(patientIdentifierConfig.label)}: {patient.identifier}
                          </span>
                        )}
                      </div>
                      <span className="font-hanken text-xs text-gray-700">
                        {formatGenderDisplay(patient.gender)} /{' '}
                        {patient.age > 0 ? `${patient.age} yrs` : '-'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Option to create a new patient with this number */}
            {onSwitchToCreatePatient && (
              <div className="rounded-lg bg-amber-50 p-3">
                <p className="mb-2 font-hanken text-sm text-amber-700">
                  Not finding the right patient? Register a new one with this number.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCreatePatient}
                  className="w-full border-amber-200 bg-white text-amber-700 hover:bg-amber-100"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Register New Patient
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PatientSearchModal;
